import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./document.module.css";
import HotlineQuestionnaireNotice from "../hotlineQuestionnaireNotice/HotlineQuestionnaireNotice";
import { useGetPendingHotlineQuestionnaireQuery } from "../../services/api/user.api";
import i18n from "../../i18n";
import StatusButton from "../statusButton/statusButton";
import {
  getPrintDate,
  getTimeFromTimestamp,
  parseQuestionAnswerString,
  getMimeType,
} from "../../utils/utils";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  useTakeInquiryMutation,
  useTakeAllInquiriesOfTypeMutation,
  useLazyGetMainPageInfoQuery,
  usePostInquiryUpdateMutation,
} from "../../services/api/user.api";
import { useNavigate } from "react-router-dom";
import {
  TParsedComment,
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../../services/types/user.types";
import { useAppSelector } from "../../services/hooks";
import {
  questionnairWithUserButtonsSelector,
  UserQuestionnairSiteLinkSelector,
  userSelector,
} from "../../services/selectors/user.selectors";
import ChangeStatusConfirmationPopup from "../modals/changeStatusConfirmationPopup/ChangeStatusConfirmationPopup";
import Modal from "../modals/modal/Modal";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ReturnInquiryConfirmationPopup from "../modals/returnInquiryConfirmation/returnInquiryConfirmationPopup";
import { noDataLabel } from "../../utils/constants";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import AppointmentButton from "../appointmentButton/AppointmentButton";
import { TQuestionnaireButton } from "../../types/types";
import FilePreview from "../filePreview/FilePreview";
import { BASE_URL_DYNAMIC } from "../../services/api/base-query";
import Tooltip from "../tooltip/tooltip";
import {
  TCallDocumentContext,
} from "../../types/callCenter.types";
import PostCallDocumentSection from "../callCenter/PostCallDocumentSection/PostCallDocumentSection";
import phoneDialIcon from "../../images/phone_prevencher_icon.svg";
import { usePaycall } from "../../contexts/PaycallContext";

function Document({
  document,
  openDocument,
  onClose,
  callData,
}: {
  document: TTherapistWithUsers | TUsersWithOutTherapist;
  openDocument: boolean;
  onClose: () => void;
  callData?: TCallDocumentContext | null;
}) {
  const navigate = useNavigate();
  const paycall = usePaycall();
  const therapist = useAppSelector(userSelector);
  const questionnairButtons = useAppSelector(
    questionnairWithUserButtonsSelector
  );
  const questionnairSiteLink = useAppSelector(UserQuestionnairSiteLinkSelector);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [takeInquiryServer, { isLoading: isLoadingInquiry }] =
    useTakeInquiryMutation();
  const [takeAllInquiries] = useTakeAllInquiriesOfTypeMutation();
  const [postInquiryUpdate] = usePostInquiryUpdateMutation();
  const [refetchMainPageInfo, { isLoading: isloadingMain }] =
    useLazyGetMainPageInfoQuery();

  const [updates, setUpdates] = useState<TParsedComment[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [textAreaValue, setTextAreaValue] = useState("");

  const [isConfirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<number | null>(null);

  const canDialPhone = Boolean(document.phone?.trim());

  // The pull, on the screen where the therapist decides whether to take this inquiry. A call
  // carries the same answer on its payload and a booked session asks when the appointment is
  // opened, but an inquiry arriving by form has neither -- it was the one route where the
  // patient was texted, the manager would be told at the close, and the therapist handling it
  // saw nothing at all.
  //
  // Before the take, deliberately: knowing they owe a depression and anxiety measure is part of
  // deciding how to pick this up, not something to discover afterwards.
  const { data: hotlineQuestionnaire } = useGetPendingHotlineQuestionnaireQuery(
    (document as any).usersId as number,
    // Asked again each time the panel opens, like the appointment modal does. Nothing
    // invalidates this cache -- the patient answers on their own device, not through anything
    // this app dispatches -- so a cached "still owes it" would outlive the fill that settled it.
    { skip: !(document as any).usersId, refetchOnMountOrArgChange: true },
  );

  const isTTherapistWithUsers = (
    document: TTherapistWithUsers | TUsersWithOutTherapist
  ): document is TTherapistWithUsers => {
    return "therapistsId" in document;
  };

  const answers: Record<string, string> | undefined = parseQuestionAnswerString(
    document.specificFields
  );

  const [isMine, setIsMine] = useState(
    isTTherapistWithUsers(document) &&
    document?.therapistsId.toString() === therapist.id.toString()
  );

  useAutosizeTextArea(textAreaRef.current, textAreaValue);

  useEffect(() => {
    if (document.comments) {
      const parsedUpdates: TParsedComment[] = JSON.parse(document.comments);
      if (parsedUpdates.length > 0) {
        setUpdates(parsedUpdates);
      }
    } else {
      setUpdates([]);
    }

    setIsMine(
      isTTherapistWithUsers(document) &&
      document?.therapistsId.toString() === therapist.id.toString()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  const onStatusChange = (newStatusId: number) => {
    setSelectedStatusId(newStatusId);
    setConfirmPopupOpen(true);
  };

  const handleConfirmSingle = () => {
    if (selectedStatusId === null) return;

    takeInquiryServer({
      Id: document.therapistsUsersId ?? 0,
      Cycle: document.cycle,
      UsersGuid: document.guid,
      TherapistsGuid: therapist.guid,
      QuestionnaireId: document.userQuestionnaireId,
      Status: selectedStatusId,
      QuestionnaireCompaniesId: document.questionnaireCompaniesId,
    })
      .then((res) => {
        // @ts-ignore
        if ("error" in res && res?.error?.originalStatus !== 200) {
          setOpenModal(true);
        } else {
          setOpenSuccessModal(true);
        }
      })
      .finally(() => {
        refetchMainPageInfo();
        setConfirmPopupOpen(false);
        setSelectedStatusId(null);
      });
  };

  const handleConfirmAll = () => {
    if (selectedStatusId === null) return;

    const requestBody = {
      UsersGuid: document.guid,
      TherapistsGuid: therapist.guid,
      QuestionnaireId: document.userQuestionnaireId,
      Status: selectedStatusId,
      QuestionnaireCompaniesId: document.questionnaireCompaniesId,
    };

    const handleApiResponse = (res: any) => {
      // @ts-ignore
      if ("error" in res && res?.error?.originalStatus !== 200) {
        setOpenModal(true);
      } else {
        setOpenSuccessModal(true);
      }
    };

    const finalActions = () => {
      refetchMainPageInfo();
      setConfirmPopupOpen(false);
      setSelectedStatusId(null);
    };

    takeAllInquiries(requestBody).then(handleApiResponse).finally(finalActions);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaValue(event.target.value);
  };
  const sendUpdate = (update: string) => {
    const creationDate = new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString();

    postInquiryUpdate({
      Id: "0",
      Cycle: document.cycle,
      UsersGuid: document.guid.toString(),
      TherapistsGuid: therapist.guid,
      QuestionnaireId: document.userQuestionnaireId,
      Comment: update,
      CreateOn: creationDate,
      QuestionnaireCompaniesId: document.questionnaireCompaniesId,
    })
      .then((res) => {
        if ("data" in res) setOpenSuccessModal(true);
        else setOpenErrorModal(true);
      })
      .catch(() => {
        setOpenErrorModal(true);
      })
      .finally(() => {
        refetchMainPageInfo();
      });
  };
  const returnInquiry = () => {
    takeInquiryServer({
      Id: document.num,
      Cycle: document.cycle,
      UsersGuid: document.guid,
      TherapistsGuid: therapist.guid,
      QuestionnaireId: document.userQuestionnaireId,
      Status: -1,
      QuestionnaireCompaniesId: document.questionnaireCompaniesId,
    })
      .then((res) => {
        // @ts-ignore
        if ("error" in res && res?.error?.originalStatus !== 200) {
          setOpenModal(true);
        } else {
          setOpenSuccessModal(true);
          setIsMine(false);
        }
      })
      .finally(() => {
        refetchMainPageInfo();
      });
  };

  const onQuestionnairButtonClick = (button: TQuestionnaireButton) => {
    if (questionnairSiteLink) {
      const url = new URL(`${questionnairSiteLink}`);
      url.searchParams.append("therapistGuid", String(therapist.guid));
      url.searchParams.append("userGuid", String(document.guid));
      url.searchParams.append(
        "questionnaireCompaniesTypeId",
        String(button.questionnaireCompaniesTypeId)
      );
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    }
  };

  const renderFiles = () => {
    if (!("files" in document) || !document.files) return null;
    let entries: { url: string; mimeType: string }[] = [];
    try {
      entries = JSON.parse(document.files);
    } catch {
      entries = document.files.split(",").map((u) => ({
        url: u.trim(),
        mimeType: getMimeType(u.trim()),
      }));
    }
    return (
      <>
        <h5 className={styles.documentSubTitle}>{t("ATTACHED_FILES")}</h5>
        <div className={styles.fileListWrapper}>
          {entries.map((entry, i) => {
            const fileName = entry.url.substring(entry.url.lastIndexOf("/") + 1);
            const fullUrl = /^https?:\/\//i.test(entry.url)
              ? entry.url
              : `${BASE_URL_DYNAMIC.replace(/\/$/, "")}/${entry.url.replace(/^\//, "")}`;
            return (
              <FilePreview
                key={entry.url}
                file={{ name: fileName, type: entry.mimeType }}
                previewUrl={fullUrl}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <article
      dir={i18n.dir()}
      className={`${styles.document} ${openDocument && styles.document_visible
        } ${callData ? styles.document_postCall : ""}`}
    >
      <button className={styles.documentCloseBtn} onClick={onClose} />
      <h4 className={styles.documentTitle}>
        {t("DOCUMENT_TITLE")} {document?.num}
      </h4>

      <HotlineQuestionnaireNotice pending={hotlineQuestionnaire?.pending} compact />
      <div className={styles.documentInfo}>
        <p className={styles.documentLabel}>
          {document?.fakeCompanyId
            ? t("DOCUMENT_LABEL_LINK")
            : t("DOCUMENT_LABEL_SUBJECT")}
          {document?.fakeCompanyId ? (
            <a
              className={styles.documentLink}
              href={`https://app.bpreven.com/${document.usersId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("DOCUMENT_LABEL_LINK")}
            >
              {t("DOCUMENT_LABEL_LINK")}
            </a>
          ) : (
            <span className={styles.documentText}>
              {document?.questionnaireCompaniesType || noDataLabel}
            </span>
          )}
        </p>

        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_OPEN_DATE")}
          <span className={styles.documentText}>
            {document?.timestamp && getPrintDate(new Date(document?.timestamp))}
          </span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_CYCLE")}
          <span className={styles.documentText}>{document?.cycle}</span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_OPEN_TIME")}
          <span className={styles.documentText}>
            {document?.timestamp && getTimeFromTimestamp(document.timestamp)}
          </span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_URGENCY")}
          <span className={styles.documentText}>
            {t("DOCUMENT_URGENCY_MEDIUM")}
          </span>
        </p>

        {answers &&
          Object.entries(answers).map(([fieldName, fieldValue]) => (
            <p className={styles.documentLabel} key={fieldName}>
              {fieldName}:
              <span className={styles.documentText}>
                {fieldValue || noDataLabel}
              </span>
            </p>
          ))}
      </div>
      <div className={styles.documentStatusLabel}>
        {t("DOCUMENT_LABEL_STATUS")}
        <StatusButton
          document={document}
          onStatusChange={onStatusChange}
          updatable={isMine}
        />
      </div>
      {isMine && renderFiles()}
      <div className={styles.documentLinks}>
        <button
          onClick={() =>
            navigate(`/users/questions_reports`, {
              state: {
                UserDetails: {
                  UsersId: document.usersId,
                  Birth: document.birth,
                  FirstName: document.firstName,
                  LastName: document.lastName,
                  Phone: document.phone,
                  Guid: document.guid,
                },
                TherapistDetails: {
                  TherapistQuestionnaireId: document.therapistQuestionnaireId,
                  QuestionnaireCompaniesType:
                    document.questionnaireCompaniesType,
                  QuestionnaireCompaniesId: document.questionnaireCompaniesId,
                  TherapistCycle: document?.cycle,
                },
              },
            })
          }
          onMouseEnter={() => setHoveredTooltip(1)}
          onMouseLeave={() => setHoveredTooltip(-1)}
          onFocus={() => setHoveredTooltip(1)}
          onBlur={() => setHoveredTooltip(-1)}
          className={`button button_white ${styles.btn} ${styles.btn_type_actionReport
            } ${!isMine && styles.btn_disabled}`}
          disabled={!isMine}
        >
          <span className={styles.btn__label}>{t("DOCUMENT_BUTTON_QUESTIONNAIRE_REPORTS")}</span>
          <Tooltip isShown={hoveredTooltip === 1} >
            {t("DOCUMENT_BUTTON_QUESTIONNAIRE_REPORTS")}
          </Tooltip>
        </button>
        <button
          disabled={!isMine || Boolean(document.therapistActionId)}
          onClick={() => {
            if ("therapistsId" in document) {
              navigate(`/users/report/`, {
                state: {
                  TherapistDetails: {
                    TherapistQuestionnaireId: document.therapistQuestionnaireId,
                    QuestionnaireCompaniesType:
                      document.questionnaireCompaniesType,
                    QuestionnaireCompaniesId: document.questionnaireCompaniesId,
                    TherapistCycle: document?.cycle,
                  },
                  UserDetails: {
                    FirstName: document.firstName,
                    LastName: document.lastName,
                    Phone: document.phone,
                    Birth: document.birth,
                    userDocument: document,
                    UsersId: document.usersId,
                    Guid: document.guid,
                  },
                },
              });
            }
          }}
          onMouseEnter={() => setHoveredTooltip(2)}
          onMouseLeave={() => setHoveredTooltip(-1)}
          onFocus={() => setHoveredTooltip(2)}
          onBlur={() => setHoveredTooltip(-1)}
          className={`button button_white ${styles.btn} ${styles.btn_type_actionReport
            } ${(!isMine || Boolean(document.therapistActionId)) &&
            styles.btn_disabled
            }`}
        >
          <span className={styles.btn__label}>{t("QUESTION_REPORTS_ACTION_REPORT_BUTTON")}</span>
          <Tooltip isShown={hoveredTooltip === 2}>
            {t("QUESTION_REPORTS_ACTION_REPORT_BUTTON")}
          </Tooltip>
        </button>
      </div>
      <h5 className={styles.documentSubTitle}>
        {t("DOCUMENT_SUBTITLE_CONTACT")}
      </h5>
      <p className={styles.documentLabel}>
        {t("DOCUMENT_LABEL_USER_NUMBER")}
        <span className={styles.documentText}>{document?.usersId}</span>
      </p>
      <p className={styles.documentLabel}>
        {t("DOCUMENT_LABEL_USER_NAME")}
        <span className={styles.documentText}>
          {document?.firstName || ""} {document?.lastName || ""}
        </span>
      </p>
      {isMine && (
        <>
          <p className={styles.documentLabel}>
            {t("DOCUMENT_LABEL_PHONE")}
            {canDialPhone ? (
              <button
                type="button"
                className={styles.documentPhoneDial}
                onClick={() => {
                  void paycall.dial(document.phone, {
                    userId: document.usersId,
                    guid: document.guid,
                    firstName: document.firstName,
                    lastName: document.lastName,
                  });
                }}
              >
                <img
                  src={phoneDialIcon}
                  alt=""
                  width={16}
                  height={16}
                  className={styles.documentPhoneIcon}
                  aria-hidden
                />
                <span
                  className={`${styles.documentText} ${styles.documentPhoneDialText}`}
                  dir="ltr"
                >
                  {document?.phone}
                </span>
              </button>
            ) : (
              <span
                className={`${styles.documentText} ${styles.documentPhoneDialText}`}
              >
                {document?.phone}
              </span>
            )}
          </p>
          <p className={styles.documentLabel}>
            {t("DOCUMENT_LABEL_EMAIL")}
            <span className={styles.documentText}>{document?.email}</span>
          </p>
        </>
      )}
      {isMine && (
        <div className={styles.documentLinks}>
          <AppointmentButton
            userId={document.usersId.toString()}
            appointmentsId={document.appointmentsId}
            media={"1"}
            name={t("APPOINTMENT_CHAT_BUTTON")}
            disabled={!isMine}
            Num={document.num}
            usersGuid={document.guid}
          />
          <AppointmentButton
            userId={document.usersId.toString()}
            appointmentsId={document.appointmentsId}
            media={"2"}
            name={t("APPOINTMENT_VIDEO_BUTTON")}
            disabled={!isMine}
            Num={document.num}
            usersGuid={document.guid}
          />
        </div>
      )}

      {callData && (
        <PostCallDocumentSection
          callData={callData}
          inquiry={document}
          onCloseDrawer={onClose}
        />
      )}

      <h5 className={styles.documentSubTitle}>
        {t("ADD_TREATMENT_PLAN_LABEL_COMMENTS")}
      </h5>
      {updates && updates.length > 0 ? (
        <ul className={styles.documentUpdates}>
          {updates
            .sort((a: TParsedComment, b: TParsedComment) => b.Id - a.Id)
            .map((upd) => (
              <li key={upd.Id}>
                <div className={styles.updateInfo}>
                  <p className={styles.documentLabel}>
                    {t("TREATMENT_DOCUMENT_UPDATE_LABEL")}
                    <span className={styles.documentText}>{upd.Id}</span>
                  </p>
                  <p className={styles.documentLabel}>
                    {t("DOCUMENT_LABEL_OPEN_DATE")}
                    <span className={styles.documentText}>
                      {(upd.CreateOn &&
                        `${getPrintDate(
                          new Date(upd.CreateOn)
                        )} ${getTimeFromTimestamp(new Date(upd.CreateOn))}`) ||
                        noDataLabel}
                    </span>
                  </p>
                </div>
                <p className={styles.documentContent}>{upd.Comment}</p>
                <p className={styles.documentLabel}>
                  {t("DOCUMENT_LABEL_UPDATED_BY")} {upd.TherapistName}
                </p>
              </li>
            ))}
        </ul>
      ) : (
        <p className={styles.noDocuments}>{t("DOCUMENT_NO_UPDATES")}</p>
      )}
      <div className={styles.textAreaContainer}>
        <textarea
          ref={textAreaRef}
          rows={1}
          name={"updates"}
          placeholder={t("DOCUMENT_TEXTAREA_PLACEHOLDER")}
          className={`${styles.documentTextArea}`}
          onChange={handleChange}
          value={textAreaValue}
        />
        <button
          className={styles.sendUpdate}
          disabled={!textAreaValue}
          onClick={() => {
            setTextAreaValue("");
            sendUpdate(textAreaValue);
          }}
        />
      </div>
      {!isMine ? (
        <button
          disabled={isMine || isLoadingInquiry}
          className={`button button_white ${styles.btn} ${isMine && styles.btn_disabled
            } hover`}
          onClick={(e) => {
            e.stopPropagation();
            takeInquiryServer({
              Id: 0,
              Cycle: document.cycle,
              UsersGuid: document.guid,
              TherapistsGuid: therapist.guid,
              QuestionnaireId: document.userQuestionnaireId,
              Status: document?.status || 1,
              QuestionnaireCompaniesId: document.questionnaireCompaniesId,
            })
              .then((res) => {
                // @ts-ignore
                if ("error" in res && res?.error?.originalStatus !== 200) {
                  setOpenModal(true);
                } else {
                  setOpenSuccessModal(true);
                  setIsMine(true);
                }
              })
              .finally(() => {
                refetchMainPageInfo();
              });
          }}
        >
          {t("DOCUMENT_BUTTON_TAKE")}
        </button>
      ) : (
        <button
          disabled={isLoadingInquiry || isloadingMain}
          className={`button button_white ${styles.btn} ${styles.btn_red} ${isLoadingInquiry || isloadingMain ? "hover" : ""
            }`}
          onClick={() => setOpenConfirmationModal(true)}
        >
          {t("DOCUMENT_BUTTON_CANCEL_TAKING_INQUIRY")}
        </button>
      )}
      <div className={`${styles.questionnairButton_container}`}>
        {questionnairButtons &&
          questionnairButtons.length !== 0 &&
          questionnairButtons.map((button, index) => (
            <button
              key={index}
              className={`button button_white ${styles.btn} hover`}
              type="button"
              onClick={() => onQuestionnairButtonClick(button)}
            >
              {button.buttonText}
            </button>
          ))}
      </div>
      {openModal && (
        <Modal
          onClose={() => {
            setOpenModal(false);
          }}
        >
          <ErrorPopup
            onClose={() => {
              setOpenModal(false);
            }}
          />
        </Modal>
      )}
      {openSuccessModal && (
        <Modal
          onClose={() => {
            setOpenSuccessModal(false);
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenSuccessModal(false);
            }}
          />
        </Modal>
      )}
      {openErrorModal && (
        <Modal
          onClose={() => {
            setOpenErrorModal(false);
          }}
        >
          <ErrorPopup
            onClose={() => {
              setOpenErrorModal(false);
            }}
          />
        </Modal>
      )}
      {openConfirmationModal && (
        <Modal
          onClose={() => {
            setOpenConfirmationModal(false);
          }}
        >
          <ReturnInquiryConfirmationPopup
            onClose={() => {
              returnInquiry();
              setIsMine(false);
              setOpenConfirmationModal(false);
            }}
          />
        </Modal>
      )}
      {isConfirmPopupOpen && (
        <Modal onClose={() => setConfirmPopupOpen(false)}>
          <ChangeStatusConfirmationPopup
            onClose={() => setConfirmPopupOpen(false)}
            onConfirmSingle={handleConfirmSingle}
            onConfirmAll={handleConfirmAll}
          />
        </Modal>
      )}
    </article>
  );
}

export default Document;
